import React, { useState, useContext, useRef } from 'react';
import { TableHead, TableRow, TableSortLabel, IconButton } from '@material-ui/core';
import MyTableCellHOC from './styled-components/MyTableCellHOC';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Bogle_Medium, REPORT_SPECIAL_KEYS, NOW, LATER, TODAY } from 'constants/constant';
import { dbSortDirection, getReportsList, dbFilterBy } from '../Dashboard.actions';
import DashboardMenu from '../components/DashboardMenu';
import DownloadIcon from '../../../assets/svgs/DownloadIcon';
import { UserPortalContext } from 'context/appcontext';
import { useDispatch, useSelector } from 'react-redux';
import dashboardStyles from '../Dashboard.style';
import { dbSortBy } from '../Dashboard.actions';

function ReportTableHeader(props) {
	const { headers, widths, height, fontHeight, isSmallScreen = false } = props;
	const [open, setOpen] = useState(false);
	const [selectedColumn, setSelectedColumn] = useState(null);
	const anchorRef = useRef(new Array());
	const portalUserContext = useContext(UserPortalContext);
	const { userDetails } = portalUserContext;
	const { companyName, loginId } = userDetails?.result;
	const dispatch = useDispatch();
	const classes = dashboardStyles({ isSmallScreen });
	const { _sortDirection, _sortBy, filterBy } = useSelector((state) => state.dashboardReducer);
	const [selectedFilter, setSelectedFilter] = useState(filterBy);

	const handleClick = (e) => {
		setOpen((prev) => !prev);
		setSelectedColumn('');
		if (!selectedFilter[selectedColumn]) {
			selectedFilter[selectedColumn] = [];
		}
		if (selectedFilter[selectedColumn].includes(e.target.textContent)) {
			let sfClone;
			/* if only one filter remaining we need to clear it out */
			if (Object.keys(selectedFilter).length > 1) {
				const idx = selectedFilter[selectedColumn].indexOf(e.target.textContent);
				sfClone = Object.assign({}, selectedFilter);
				sfClone[selectedColumn].splice(idx, 1);
			} else {
				delete selectedFilter[selectedColumn];
				sfClone = Object.assign({}, selectedFilter);
			}
			Object.keys(sfClone).forEach((key) => {
				if (sfClone[key].length === 0) {
					delete sfClone[key];
				}
			});

			setSelectedFilter(sfClone);
			dispatch(dbFilterBy(sfClone));
		} else {
			if (e.target.textContent === 'All') {
				delete selectedFilter[selectedColumn];
			} else {
				/**
				 * Special case
				 * If user selects Frequency = Once for filter, it should include cases - 'Now', 'Later', 'Today'
				 * When user deselect the same filter item, entry should be removed from `selectedFilter`
				 */
				if (selectedColumn === 'Frequency' && e.target.textContent === 'Once') {
					const onceValues = [NOW, LATER, TODAY];
					if (selectedFilter[selectedColumn].includes(...onceValues)) {
						selectedFilter[selectedColumn] = selectedFilter[selectedColumn].filter(
							(value) => !onceValues.includes(value)
						);
					} else {
						selectedFilter[selectedColumn] = [...onceValues];
					}
					const filter = { ...selectedFilter };
					Object.keys(filter).forEach((key) => {
						if (filter[key].length === 0) {
							delete filter[key];
						}
					});
					setSelectedFilter(Object.assign({}, filter));
					dispatch(dbFilterBy(Object.assign({}, filter)));
					return;
				}
				selectedFilter[selectedColumn] = [e.target.textContent];
			}
			const merged = Object.assign({}, selectedFilter);
			setSelectedFilter(merged);
			dispatch(dbFilterBy(merged));
		}
	};

	const handleClickAway = (event) => {
		if (anchorRef.current) {
			anchorRef.current
				.filter((item) => item?.name?.toLowerCase() === selectedColumn.toLowerCase())
				.map((itm) => {
					if (itm.contains(event.target)) {
						return;
					}
				});
		}
		setOpen(false);
		setSelectedColumn('');
	};

	function handleListKeyDown(event) {
		if (event.key === 'Tab') {
			event.preventDefault();
			setOpen(false);
		}
	}

	const getHeaders = () => {
		return headers.map((item, index) => {
			if (item.sortBy) {
				return (
					<MyTableCellHOC
						key={`${item.name}-${index}`}
						name={`${item.name}`}
						width={widths[index]}
						height={height}
						style={{ whiteSpace: 'nowrap' }}
						role={'columnheader'}
						aria-label={`${item.name}, press enter to reverse the sort order`}
						aria-sort={_sortBy === item.sortBy ? 'descending' : 'ascending'}
						onKeyDown={(e) => {
							if (REPORT_SPECIAL_KEYS.includes(e.key)) {
								return;
							}
							dispatch(dbSortBy(item.sortBy));
							dispatch(dbSortDirection(_sortDirection));
						}}
						fontSize={fontHeight}>
						<TableSortLabel
							style={{ fontSize: fontHeight, fontFamily: Bogle_Medium, fontWeight: 400 }}
							tabIndex="-1"
							id={`sort-${item.sortBy}`}
							active={_sortBy === item.sortBy}
							direction={_sortBy === item.sortBy ? _sortDirection : 'asc'}
							onClick={(e) => {
								if (REPORT_SPECIAL_KEYS.includes(e.key)) {
									return;
								}
								dispatch(dbSortBy(item.sortBy));
								dispatch(dbSortDirection(_sortDirection));
							}}
							classes={{ icon: classes.sortLabel }}>
							{item.name}
						</TableSortLabel>
					</MyTableCellHOC>
				);
			} else {
				return (
					<MyTableCellHOC
						width={widths[index]}
						height={height}
						style={{ whiteSpace: 'nowrap' }}
						fontSize={fontHeight}
						key={`item.name-${index}`}>
						<>
							{item.name || (
								<IconButton
									id="btn-new-report"
									onClick={() => {
										dispatch(getReportsList({ requestBody: loginId, companyName }));
									}}>
									<RefreshIcon className={classes.refresh} fontSize="medium" />
								</IconButton>
							)}
							{!item.name || (
								<IconButton
									className={classes.filterIcon}
									id={`${item.name}`}
									name={`${item.name}`}
									ref={(element) => anchorRef.current.splice(index, 1, element)}
									aria-controls={open ? 'menu-list-grow' : undefined}
									aria-haspopup="true"
									onClick={() => {
										setOpen(true);
										setSelectedColumn(item.name);
									}}>
									<DownloadIcon />
								</IconButton>
							)}
						</>
						<DashboardMenu
							selectedColumn={selectedColumn}
							onKeyDown={handleListKeyDown}
							anchorEl={anchorRef.current[index]}
							handleClick={handleClick}
							handleClose={handleClickAway}
							open={open}
							selectedFilter={selectedFilter}
						/>
					</MyTableCellHOC>
				);
			}
		});
	};

	return (
		<TableHead>
			<TableRow>{getHeaders()}</TableRow>
		</TableHead>
	);
}

export default ReportTableHeader;
